import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { formatCurrency } from '../../shared/format_currency.js';
import type {
  CheckoutItemSource,
  CheckoutMoney,
  CheckoutSubmitBlocker,
  CheckoutTravelerRef,
} from '../model/checkout_source.js';
import {
  checkoutScreenComposition,
  type CheckoutScreenComposition,
} from '../products/checkout_screen.js';
import { CheckoutShell } from '../shell/checkout_shell.js';
import { blockersMessage } from './checkout_price_panel.js';
import type { CheckoutSubmitNotice } from './checkout_subject.js';
import { useCheckoutEnv } from './env_context.js';
import { TripSummaryPanel } from './trip_summary_panel.js';

export interface CheckoutDrawerShellProps {
  phase: 'loading' | 'ready' | 'error';
  /** Items rendered in the side "Sua viagem" panel. */
  items: CheckoutItemSource[];
  /** A viagem deste checkout (#3180): com ela o painel lateral também lista os
   *  serviços já comprados dela, fora da conta do total. */
  tripId?: string;
  /** Viajantes da reserva — a linha de meta do cartão lateral. */
  travelers?: CheckoutTravelerRef[];
  /** Unit price shown for multi-traveler bookings. */
  perTraveler?: CheckoutMoney;
  /** Cart total shown in the price panel. */
  total: CheckoutMoney;
  /** Daily/nightly count for the price panel (rental/hotel); absent otherwise. */
  units?: number;
  submitting: boolean;
  canSubmit: boolean;
  /** O que ainda trava o CTA (nomeado junto a ele); ausente ⇒ só o cinza. */
  submitBlockers?: CheckoutSubmitBlocker[];
  /** Faixa âmbar inline do erro determinado (repassada ao painel lateral). */
  submitNotice?: CheckoutSubmitNotice;
  onSubmit: () => void;
  /** Refaz a leitura que pôs a casca em `error` (#6644). */
  onRetry?: () => void;
  onClose: () => void;
  /** Main-column content — rendered only in the `ready` phase. */
  children: ReactNode;
  /** O checkout de CONCIERGE (`concierge_checkout`) troca o título, o chip de
   *  contexto e a coluna lateral inteira: não há carrinho para derivá-los, e
   *  o painel de preço não faz sentido sem preço. Os três são opcionais e,
   *  ausentes, o chrome é byte a byte o do checkout online. */
  title?: string;
  chip?: string;
  side?: ReactNode;
  /** A tela do produto já calculada por quem monta a casca (`OfferCheckout`);
   *  ausente, a casca a deriva de `items`. */
  composition?: CheckoutScreenComposition;
}

/** O chrome do checkout: o `CheckoutShell` com a copy do host,
 *  o chip derivado do carrinho, a telemetria de erro e, na coluna lateral, o cartão
 *  "Sua viagem" (ou o `side` do modo pedido). Os dois modos renderizam o form em
 *  `children`. */
export function CheckoutDrawerShell({
  phase,
  items,
  tripId,
  travelers,
  perTraveler,
  total,
  units,
  submitting,
  canSubmit,
  submitBlockers,
  submitNotice,
  onSubmit,
  onRetry,
  onClose,
  children,
  title: titleOverride,
  chip: chipOverride,
  side,
  composition: precomputed,
}: Readonly<CheckoutDrawerShellProps>) {
  const env = useCheckoutEnv();
  const { copy, locale } = env;
  const dockBlockersId = useId();
  // O mesmo motivo do resumo, dito ao leitor de tela no botão travado da barra.
  // O produto compõe o chip de contexto do header (produto + rota e janela de
  // datas, quando o fio as traz) e o nome do motivo nas pendências.
  const composition = precomputed ?? checkoutScreenComposition(items, env);
  const dockBlockers =
    phase === 'ready' && !canSubmit && !submitting
      ? blockersMessage(copy, locale, submitBlockers, composition.motive)
      : undefined;
  const { track } = env;
  const title = titleOverride ?? copy.shell.title;
  const ctx = composition.context;
  const chip = chipOverride ?? [ctx.chip, ctx.route, ctx.dates].filter(Boolean).join(' · ');

  // Telemetria: uma vez por TRANSIÇÃO para `error`, nunca por render — o ref
  // guarda a fase anterior pra distinguir "acabou de entrar em erro" de
  // "segue em erro" (um re-render por outro prop não pode reemitir o evento).
  const prevPhase = useRef<CheckoutDrawerShellProps['phase'] | undefined>(undefined);
  useEffect(() => {
    if (phase === 'error' && prevPhase.current !== 'error') {
      track('error_shown', { kind: 'checkout_phase_error' });
    }
    prevPhase.current = phase;
  }, [phase, track]);

  const dock = (reveal: () => void) => (
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-muted-foreground m-0 text-xs">{copy.shell.dockTotal}</p>
        <p className="m-0 text-base font-semibold tabular-nums">
          {formatCurrency(total.amount, total.currency, locale)}
        </p>
      </div>
      <Button
        size="lg"
        className="rounded-full px-6"
        disabled={submitting}
        // Travado, o toque leva ao resumo, onde mora o que falta
        // preencher; liberado, solicita como o botão de lá.
        onClick={phase === 'ready' && canSubmit ? onSubmit : reveal}
        aria-describedby={dockBlockers && dockBlockersId}
      >
        {copy.shell.dockSubmit}
      </Button>
      {dockBlockers && (
        <span id={dockBlockersId} className="sr-only">
          {dockBlockers}
        </span>
      )}
    </div>
  );

  return (
    <CheckoutShell
      phase={phase}
      title={title}
      chip={chip}
      copy={copy.shell}
      {...(onRetry ? { onRetry } : {})}
      onClose={onClose}
      // A barra do celular (#5150) só no checkout com carrinho: o de pedido
      // (`side` próprio) não tem total para mostrar.
      {...(side === undefined
        ? {
            dock,
          }
        : {})}
      side={
        side ?? (
          <TripSummaryPanel
            items={items}
            composition={composition}
            tripId={tripId}
            travelers={travelers}
            perTraveler={perTraveler}
            total={total}
            units={units}
            submitNotice={submitNotice}
            onSubmit={onSubmit}
            // O Voltar do rodapé é o MESMO onClose do ✕ — no touch o topbar
            // fica longe do polegar, e a decisão de descartar segue no host.
            onBack={onClose}
            submitting={submitting}
            // Cinto no contrato do shell: enquanto o form não renderizou
            // não há o que submeter, ainda que o host mande true.
            canSubmit={phase === 'ready' && canSubmit}
            {...(phase === 'ready' && submitBlockers ? { submitBlockers } : {})}
          />
        )
      }
    >
      {children}
    </CheckoutShell>
  );
}
